import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Cards,
  ColumnDatas,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  Table,
  Tooltip,
  Typography,
} from "fds";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import dayjs from "dayjs";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import {
  Member,
  Workgroup,
  WorkgroupRespDto,
  WorkgroupSearchDto,
} from "../../types/workgroup";
import AvatarGroup from "fds/src/components/atom/Avatargroup";
import WorkgroupSettingButton from "../../components/WorkgroupSettingButton";
import { useNavigate } from "react-router-dom";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";
import EmptyInitFilterPage from "../../components/EmptyPage/EmptyInitFilterPage";
import WorkgroupCard from "../../components/WorkgroupCard";
import WorkgroupAddModal from "../../components/WorkgroupAddModal";
import DateDropdown from "../../components/DateDropdown";
import { t } from "i18next";
import { Trans } from "react-i18next";
import UserSearchDropdown from "../../components/UserSearchDropdown";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";

const WorkgroupListPage = () => {
  const SORT_TYPE = [
    { label: t("common.sortDateNewFirst"), key: "updateDate_DESC" },
    { label: t("common.sortDateNewLast"), key: "updateDate_ASC" },
    { label: t("common.sortDateNameDesc"), key: "workgroupName_DESC" },
    { label: t("common.sortDateNameAsc"), key: "workgroupName_ASC" },
  ];
  const INIT_FILTER = {
    isDetail: "Y",
    managerId: "",
    orderBy: "workgroupName",
    orderByType: "ASC",
    pageRange: 1,
    posPage: 1,
    rowRange: 50,
    searchDate: {
      start: 0,
      end: 0,
    },
    searchMemberIds: [],
    status: 1,
    workgroupName: "",
  };
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  /** Hook 정리 */
  const navigate = useNavigate();
  const { workgroupState, getWorkgroupFullList } = useWorkgroupStore();
  const workgroupListNew = useRef([] as ColumnDatas[]);
  const workgroupListNewCard = useRef([] as any[]);

  /** State 정리 */
  const [workgroupSettingInfo, setWorkgroupSettingInfo] = useState<{
    x: number;
    y: number;
    id: string;
    workgroupInfo?: Workgroup;
  }>({
    x: 0,
    y: 0,
    id: "",
    workgroupInfo: undefined,
  }); // 워크그룹 더보기 메뉴 setting
  const [docShowType, changeDocShowType] = useState<"smallCard" | "list">(
    window.eco.listView === "list" ? "list" : "smallCard"
  ); // 워크그룹 리스트 표시 방법
  const [workgroupListInfo, changeWorkgroupListInfo] =
    useState<WorkgroupRespDto>(); // 워크그룹 리스트 정보
  const [key, setKey] = useState(0); // state 수정시 table업데이트용
  const [buttonClick, setButtonClick] = useState(0);
  const [dateFilter, changeDateFilter] = useState<string>("all"); // 달력 필터 표시
  const [filter, changeFilters] = useState<WorkgroupSearchDto>(INIT_FILTER); // 워크그룹 리스트 호출시 사용되는 필터
  const [users, changeUsers] = useState<Member[]>([]); // 멤버 검색 필터시 사용되는 id 리스트
  const [savedUsers, changeSavedUsers] = useState<Member[]>([]); // 멤버 검색 필터시 사용되는 userID 저징
  const [openCreateWorkgroupModal, toggleOpenCreateWorkgroupModal] =
    useState(false); // 워크그룹 생성 모달 열고닫기
  const [searchKey, changeSearchKey] = useState(""); // 워크그룹 이름 검색 값

  /** useEffect정리 */
  useEffect(() => {
    changeWorkgroupListInfo(workgroupState.workgroupListInfo);
    let newDataContent = [] as ColumnDatas[];
    let newDataCardContent = [] as any[];
    workgroupState.workgroupList?.forEach((item: Workgroup, index: number) => {
      newDataContent.push(getTableData(item, index));
      newDataCardContent.push(getTableCardData(item, index));
    });
    workgroupListNew.current = newDataContent;
    workgroupListNewCard.current = newDataCardContent;
    setKey(key + 1);
  }, [workgroupState.workgroupList]);
  useEffect(() => {
    getWorkgroupFullList(filter);
  }, [filter]);

  /** 함수 정리 */
  /** 워크그룹 필터 수정 */
  const changeFilterByKey = (key: string, value: any) => {
    let newValue = value;
    document.getElementById("docTable")?.scrollTo(0, 0);
    if (key === "order") {
      if (value.includes("_direct")) {
        newValue = value.split("_");
        changeFilters({
          ...filter,
          orderBy: newValue[0],
          orderByType: newValue[1],
          posPage: 1,
        });
        return;
      } else {
        if (value === "workgroupName") {
          if (filter.orderBy.includes("updateDate")) {
            newValue = "ASC";
          } else {
            newValue = filter.orderByType === "ASC" ? "DESC" : "ASC";
          }
          changeFilters({
            ...filter,
            orderBy: "workgroupName",
            orderByType: newValue,
            posPage: 1,
          });
          return;
        } else {
          if (filter.orderBy.includes("workgroupName")) {
            newValue = "ASC";
          } else {
            newValue = filter.orderByType === "ASC" ? "DESC" : "ASC";
          }
          changeFilters({
            ...filter,
            orderBy: "updateDate",
            orderByType: newValue,
            posPage: 1,
          });
          return;
        }
      }
    } else if (key === "searchDate") {
      if (typeof value !== "object") {
        changeDateFilter(value);
        changeFilters({
          ...filter,
          searchDate: {
            start: utilsCommon.getStartPeriod(value),
            end: value === "all" ? 0 : dayjs().valueOf(),
          },
          posPage: 1,
        });
      } else {
        changeDateFilter("custom");
        changeFilters({
          ...filter,
          searchDate: {
            start: dayjs(value.start).valueOf(),
            end: dayjs(value.end).valueOf(),
          },
          posPage: 1,
        });
      }
      return;
    } else if (key === "searchMemberIds") {
      changeSavedUsers(users);
    }
    changeFilters({ ...filter, [key]: newValue, posPage: 1 });
  };
  /** 워크그룹 필터 초기화상태인지 체크 */
  const isInitFilter = () => {
    return (
      dayjs(INIT_FILTER.searchDate.start).diff(filter.searchDate.start, "d") ===
        0 &&
      dayjs(INIT_FILTER.searchDate.end).diff(filter.searchDate.end, "d") ===
        0 &&
      (!filter.searchMemberIds || filter.searchMemberIds.length === 0) &&
      filter.workgroupName === ""
    );
  };
  /** 워크그룹 필터 초기화 */
  const handleInitFilter = () => {
    changeFilters(INIT_FILTER);
    changeDateFilter("all");
    changeSearchKey("");
    changeSavedUsers([]);
    changeUsers([]);
  };
  /** UI: 카드 리스트 UI */
  const getTableCardData = (row: Workgroup, index: any) => {
    const validOpacity = row.isValidate === 0 ? "20%" : "100%";
    return {
      id: row?.workgroupId,
      data: (
        <WorkgroupCard
          row={row}
          opacity={validOpacity}
          setWorkgroupSettingInfo={setWorkgroupSettingInfo}
        />
      ),
    };
  };
  /** UI: 워크그룹 리스트 UI */
  const getTableData = (row: Workgroup, index: any): ColumnDatas => {
    const validOpacity = row.isValidate === 0 ? "20%" : "100%";
    return {
      clickAction: () =>
        navigate(
          `${process.env.PUBLIC_URL}/workgroup/${row?.workgroupId}/home`
        ),
      clickRAction: (e) => {
        e.stopPropagation();
        if (row.isValidate !== 0)
          setWorkgroupSettingInfo({
            x: e.pageX - 240,
            y: e.pageY - 61,
            id: row?.workgroupId,
            workgroupInfo: row,
          });
      },
      data: [
        {
          columnText: (
            <Box classes={"mg ml-auto"} width={"fit-content"}>
              {row?.favorite === "Y" && (
                <Icons
                  variant="fill"
                  label="star"
                  isColored
                  size={24}
                  style={{ opacity: validOpacity }}
                />
              )}
            </Box>
          ),
        },
        {
          columnText: (
            <Box classes={"mg ml-auto"} width={"fit-content"}>
              {" "}
            </Box>
          ),
        },
        {
          columnText: (
            <Box>
              <Avatar
                name={row?.workgroupName}
                size="40"
                shape="workgroup"
                uniqueCode={row?.workgroupId}
                opacity={row.isValidate === 0 ? "20%" : "100%"}
              />
              <Box
                classes="mg ml-12"
                maxWidth="calc(100% - 52px)"
                direction="column"
                justifyContent="center"
              >
                <Tooltip message={row?.workgroupName}>
                  <Typography
                    type="body2_rg"
                    ellipsis
                    exactColor={
                      row.isValidate === 0
                        ? getColor("COLOR_TEXT_DISABLED")
                        : undefined
                    }
                  >
                    {row?.workgroupName}
                  </Typography>
                </Tooltip>
                {row.isValidate === 0 ? (
                  <Typography
                    type="caption_rg"
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("workgroup.expired.title")}
                  </Typography>
                ) : (
                  row?.desc !== "" && (
                    <Tooltip message={row?.desc}>
                      <Typography
                        type="body4_rg"
                        ellipsis
                        exactColor={getColor("COLOR_TEXT_SECONDARY")}
                      >
                        {row?.desc}
                      </Typography>
                    </Tooltip>
                  )
                )}
              </Box>
            </Box>
          ),
        },
        {
          columnText: (
            <Box>
              <Box width={32} alignItems="center" justifyContent="center">
                <Icons
                  variant="line"
                  label="alert"
                  style={{ opacity: validOpacity }}
                  stroke={getColor(
                    row?.readRequestCount === 0 || row.isValidate === 0
                      ? "COLOR_ICON_INTERACTIVE_PRIMARY_DISABLED"
                      : "COLOR_TEXT_PRIMARY"
                  )}
                />
              </Box>
              <Typography
                type="body2_rg"
                width={20}
                style={{ overflow: "visible" }}
                whiteSpace="normal"
                exactColor={
                  row.isValidate === 0
                    ? getColor("COLOR_TEXT_DISABLED")
                    : undefined
                }
              >
                {row?.readRequestCount > 0 ? row?.readRequestCount : ""}
              </Typography>
              <Box width={32} alignItems="center" justifyContent="center">
                <Icons
                  variant="line"
                  label="zoom"
                  style={{ opacity: validOpacity }}
                  stroke={getColor(
                    row?.webMeetingCount === 0 || row.isValidate === 0
                      ? "COLOR_ICON_INTERACTIVE_PRIMARY_DISABLED"
                      : "COLOR_TEXT_PRIMARY"
                  )}
                />
              </Box>
              <Typography
                type="body2_rg"
                width={20}
                style={{ overflow: "visible" }}
                whiteSpace="normal"
                exactColor={
                  row.isValidate === 0
                    ? getColor("COLOR_TEXT_DISABLED")
                    : undefined
                }
              >
                {row?.webMeetingCount > 0 ? row?.webMeetingCount : ""}
              </Typography>
            </Box>
          ),
        },
        {
          columnText: (
            <Tooltip
              message={row?.members
                ?.map((item) => item?.userName || item.userId)
                .join(", ")}
            >
              <AvatarGroup
                visibleLength={8}
                size={"24"}
                opacity={validOpacity}
                items={utilsCommon.sortMembersToAvatarGroup(
                  row?.members,
                  row?.managerId
                )}
              />
            </Tooltip>
          ),
        },
        {
          columnText: (
            <Box alignItems="center">
              <Typography
                type="body2_rg"
                style={{ overflow: "visible" }}
                whiteSpace="normal"
                exactColor={
                  row.isValidate === 0
                    ? getColor("COLOR_TEXT_DISABLED")
                    : undefined
                }
              >
                {utilsCommon.renderDateFormat(row?.updateDate)}
              </Typography>
              {row.isValidate !== 0 && (
                <Button
                  btnStyle={"table-icon"}
                  size="sm"
                  classes="mg ml-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    setWorkgroupSettingInfo({
                      x: e.pageX - 240,
                      y: e.pageY - 61,
                      id: row?.workgroupId,
                      workgroupInfo: row,
                    });
                  }}
                  leadingIcon={<Icons variant="line" label="moreVertical" />}
                />
              )}
            </Box>
          ),
        },
      ],
      id: row?.workgroupId,
    };
  };
  /** UI: 필터 */
  const renderFilter = () => {
    return (
      <Box classes="workgrouplist-filter">
        <Button
          leadingIcon={<Icons variant="line" label="redoLeft" />}
          btnStyle="icon"
          classes="mg mt-4"
          size={"sm"}
          state={isInitFilter() ? "disable" : "default"}
          onClick={handleInitFilter}
        />
        <UserSearchDropdown
          buttonClick={buttonClick}
          filter={filter}
          users={users}
          savedUsers={savedUsers}
          isAllowEmptyUser
          changeUsers={changeUsers}
          changeFilterByKey={changeFilterByKey}
          setButtonClick={setButtonClick}
        />
        <DateDropdown
          buttonClick={buttonClick}
          dateFilter={dateFilter}
          filter={filter}
          placeholder={t("file.lastUpdate")}
          changeFilterByKey={(key, val) => changeFilterByKey("searchDate", val)}
          setButtonClick={() => setButtonClick(buttonClick + 1)}
        />
        <InputField
          placeholder={t("workgroup.searchWorkgroupPlaceholder")}
          leadingIcon={<Icons variant="line" label="search" />}
          classes="mg ml-auto"
          size="md"
          maxLength={120}
          onKeyUp={(e) =>
            e.code === "Enter" &&
            changeFilterByKey("workgroupName", e.target.value)
          }
          value={searchKey}
          onChange={(e) => changeSearchKey(e.target.value)}
        />
      </Box>
    );
  };
  /** UI: 워크그룹 카드 내부 */
  const renderCard = () => {
    return (
      <>
        <Box classes="cardHeader" style={{ margin: "0 32px 16px 32px" }}>
          <Dropdown
            btnStyle="link"
            placeholder=" "
            value={
              SORT_TYPE.filter(
                (item) =>
                  item.key.includes(filter?.orderBy) &&
                  item.key.includes(filter.orderByType)
              )[0]?.label
            }
            buttonClick={key}
          >
            {SORT_TYPE.map((item) => (
              <DropdownItem
                type="check"
                checkColor={
                  item.key.includes(filter?.orderBy) &&
                  item.key.includes(filter.orderByType)
                }
                checked={
                  item.key.includes(filter?.orderBy) &&
                  item.key.includes(filter.orderByType)
                }
                label={item.label}
                onClick={() => {
                  setKey(key + 1);
                  changeFilterByKey &&
                    changeFilterByKey("order", `${item.key}_direct`);
                }}
              />
            ))}
          </Dropdown>
        </Box>
        <Cards
          backgroundColorType="white"
          width={"calc(100% - 64px)"}
          height={
            workgroupListNew.current.length === 0
              ? undefined
              : "calc(-330px + 100vh)"
          }
          targetId={workgroupSettingInfo.id}
          size={"smd"}
          data={workgroupListNewCard.current}
          endReached={() =>
            workgroupListInfo?.total &&
            workgroupListInfo?.total >
              workgroupListInfo?.workgroupList.length &&
            changeFilters({ ...filter, posPage: filter.posPage + 1 })
          }
          classes="documentBottom-cards"
        />
      </>
    );
  };
  /** UI: 워크그룹 리스트 내부 */
  const renderTable = () => {
    return (
      <Table
        width={"calc(100% - 64px)"}
        header={[
          {
            headerText: "",
            width: "56px",
            order: "",
            orderAction: () => {},
          },
          {
            headerText: "",
            width: "16px",
          },
          {
            headerText: t("common.workgroupName"),
            width: "calc((100% - 512px) * 2 / 3)",
            order:
              filter.orderBy === "workgroupName" && filter.orderByType === "ASC"
                ? "upOn"
                : filter.orderBy === "workgroupName" &&
                  filter.orderByType !== "ASC"
                ? "downOn"
                : "downOff",
            orderAction: () => changeFilterByKey("order", "workgroupName"),
          },
          {
            headerText: "My Tasks",
            width: "200px",
            order: "",
          },
          {
            headerText: t("common.member"),
            width: "240px",
            order: "",
            orderAction: () => {},
          },
          {
            headerText: t("file.lastUpdate"),
            width: "calc((100% - 512px) * 1 / 3)",
            order:
              filter.orderBy === "updateDate" && filter.orderByType === "ASC"
                ? "upOn"
                : filter.orderBy === "updateDate" &&
                  filter.orderByType === "DESC"
                ? "downOn"
                : "downOff",
            orderAction: () => changeFilterByKey("order", "searchDate"),
          },
        ]}
        targetId={workgroupSettingInfo.id}
        asideColor={"theme"}
        columnHeight={"lg"}
        height={
          workgroupListNew.current.length === 0
            ? undefined
            : "calc(100vh - 318px)"
        }
        endReached={() =>
          workgroupListInfo?.total &&
          workgroupListInfo?.total > workgroupListInfo?.workgroupList.length &&
          changeFilters({ ...filter, posPage: filter.posPage + 1 })
        }
        data={workgroupListNew.current}
        totalCount={workgroupListNew.current.length}
        border={"boundary"}
        classes="workgroupList"
      />
    );
  };
  return (
    <Box width={"100%"} height={"100%"} classes={"workgrouplist body"}>
      <Box height={96} classes={"layout-title"}>
        {openCreateWorkgroupModal && (
          <WorkgroupAddModal
            createType={"CREATE_NEW_WORKGROUP"}
            open={true}
            onClose={() => toggleOpenCreateWorkgroupModal(false)}
          />
        )}
        {workgroupSettingInfo.id && (
          <WorkgroupSettingButton
            workgroupSettingInfo={workgroupSettingInfo}
            setWorkgroupSettingInfo={setWorkgroupSettingInfo}
          />
        )}
        <Typography type="h1">{t("common.workgroup")}</Typography>
        {utilsCommon.checkWorkgroupCreatePermission(loginDto) && (
          <Button
            label={t("workgroup.create")}
            btnStyle="fill"
            type="secondary"
            size="lg"
            leadingIcon={<Icons variant="line" label="plusCircle" />}
            onClick={() => toggleOpenCreateWorkgroupModal(true)}
          />
        )}
      </Box>
      {renderFilter()}
      <Box minHeight={44} alignItems="center">
        <Typography type="subtitle2">
          <Trans
            i18nKey="workgroup.withCount"
            values={{ count: workgroupListInfo?.total ?? 0 }}
            components={{
              1: (
                <Typography
                  type="subtitle2"
                  exactColor={getColor("COLOR_TEXT_BRAND")}
                />
              ),
            }}
          />
        </Typography>
        <Box classes="mg ml-auto">
          {(["list", "smallCard"] as ("smallCard" | "list")[]).map((item) => (
            <Button
              btnStyle={"icon"}
              onClick={() => changeDocShowType(item)}
              leadingIcon={
                <Icons
                  variant="line"
                  label={item === "list" ? "list" : "grid"}
                  stroke={
                    docShowType === item
                      ? getColor("COLOR_ICON_BRAND")
                      : undefined
                  }
                />
              }
            />
          ))}
        </Box>
      </Box>
      {docShowType === "list" ? renderTable() : renderCard()}
      {workgroupListNew.current.length === 0 &&
        (JSON.stringify(filter) === JSON.stringify(INIT_FILTER) ? (
          <EmptyInitFilterPage
            pageType={"workgroups"}
            handleClickButton={() => toggleOpenCreateWorkgroupModal(true)}
          />
        ) : (
          <EmptyFilterPage handleInitFilter={handleInitFilter} />
        ))}
    </Box>
  );
};

export default WorkgroupListPage;
